import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import {
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { groupsApi, organizationsApi } from "api";
import { useOutletContext } from "react-router-dom";
import { getTranslation, toDataUrl, OutletType } from "common";
import { useBreakpoint, useInfiniteScroll, useSnackbar, useTranslations, useVersions } from "hooks";
import { Add, Delete, Edit } from "@mui/icons-material";
import { GroupDialog } from "./dialogs";
import AvatarRem from "./AvatarRem";
import FlowentiButton from "./flowenti-ui-kit/FlowentiButton";
import OrganizationResourceTable, {
  OrganizationResourceCards,
  OrganizationResourceColumn,
} from "pages/organizations/OrganizationResources";
import groupsEmptyImage from "image/empty-groups.png";
import { SupportedUserDataTypes } from "hooks/useUserDataKeys";

export interface Group {
  creation?: string;
  icon?: string | null;
  id?: number;
  idorganization?: number;
  name?: string;
  userscount?: number;
  administrators?: string[];
  [key: string]: any;
}

interface GroupsPanelProps {
  organizationId: number;
  administrator: boolean;
  onCreateGroup?: () => void;
}
type Ref = { getGroups: Function } | undefined;

const INITIAL_PAGE = 1;
const PAGE_SIZE = 10;

const getUsersCount = (resource: any) =>
  Number(
    resource.usersnumber ??
    resource.userscount ??
    resource.usercount ??
    resource.members ??
    resource.memberscount ??
    resource.users?.length ??
    0
  );

const getAdministratorsLabel = (group: Group) => {
  const admins =
    group.administrators ||
    group.admins ||
    group.hods ||
    group.users?.filter((user: any) => user.administrator || user.hod) ||
    [];

  if (!Array.isArray(admins) || !admins.length) return "-";

  return admins
    .slice(0, 2)
    .map((admin: any) => {
      if (typeof admin === "string") {
        return admin;
      }

      return (
        [admin.firstname, admin.lastname].filter(Boolean).join(" ") ||
        admin.email ||
        admin.name
      );
    })
    .filter(Boolean)
    .join(", ");
};

const GroupsPanel = forwardRef<Ref, GroupsPanelProps>(
  ({ organizationId, administrator, onCreateGroup }, ref) => {
    const translations = useTranslations();
    const [groupEdit, setGroupEdit] = useState<Group | null>(null);
    const [groups, setGroups] = useState<Array<Group>>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const { secondFilter: groupsFilter } = useOutletContext<OutletType>();
    const [page, setPage] = useState<number>(INITIAL_PAGE);
    const [hasMore, setHasMore] = useState<boolean>(true);
    const { sendSnack } = useSnackbar();
    const lastGroupRef = useInfiniteScroll(setPage, loading, hasMore);
    const mount = useRef<boolean>(false);
    const { updateVersion, getVersion } = useVersions();
    const isMobile = useBreakpoint("down", "sm");

    const getFirstGroups = useCallback(async () => {
      setLoading(true);
      try {
        const queryPage = `?page=${INITIAL_PAGE}&pagesize=${PAGE_SIZE}`;
        const filter = `&name=${groupsFilter}`;
        const { data } = await organizationsApi.get(
          `${organizationId}/groups/v2${queryPage}${filter}`
        );
        setHasMore(Number(data.pagination.curr_page) < Number(data.pagination.total_pages));
        setGroups([...data.groups]);
        setPage(INITIAL_PAGE);
      } catch {
      } finally {
        setLoading(false);
      }
    }, [organizationId, groupsFilter]);

    const getGroups = useCallback(
      async (query: string) => {
        if (page !== INITIAL_PAGE) {
          setLoading(true);
          try {
            const queryPage = `?page=${page}&pagesize=${PAGE_SIZE}`;
            const filter = `&name=${query}`;
            const { data } = await organizationsApi.get(
              `${organizationId}/groups/v2${queryPage}${filter}`
            );
            setHasMore(Number(data.pagination.curr_page) < Number(data.pagination.total_pages));
            setGroups((_groups) => [..._groups, ...data.groups]);
          } catch {
          } finally {
            setLoading(false);
          }
        }
      },
      [organizationId, page]
    );

    useImperativeHandle(
      ref,
      () => ({
        getGroups: getFirstGroups,
      }),
      [getFirstGroups]
    );

    useEffect(() => {
      getGroups(groupsFilter);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getGroups]);

    useEffect(() => {
      if (organizationId && mount.current) {
        getFirstGroups();
      }
      else if (!mount.current) {
        mount.current = true;
        getFirstGroups();
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [getFirstGroups]);

    // useEffect(() => {
    //   if (!mount.current) {
    //     mount.current = true;
    //     getFirstGroups();
    //   }
    //   // eslint-disable-next-line react-hooks/exhaustive-deps
    // }, [organizationId]);

    useEffect(() => {
      return () => {
        mount.current = false;
        setPage(INITIAL_PAGE);
        setGroups([]);
      };
    }, [organizationId]);

    const handleSaveEdit = async (
      imageUrl: string | null,
      name: string | null
    ) => {
      try {
        let icon = null;
        if (imageUrl) {
          icon = await toDataUrl(imageUrl);
        }
        const updateApi = await groupsApi.put(`${groupEdit?.id}`, { name, icon });
        updateVersion(groupEdit?.id);
        setGroupEdit(null);
        sendSnack({
          message: `${updateApi.data.responsemessage}`,
          type: "success",
        });
        getFirstGroups();
      } catch {
      } finally {
      }
    };

    const handleDeleteGroup = async (targetGroup: Group | null = groupEdit) => {
      if (targetGroup) {
        try {
          const deleteApi = await groupsApi.delete(`${targetGroup.id}`);
          sendSnack({
            message: `${deleteApi.data.responsemessage}`,
            type: "success",
          });
          getFirstGroups();
          setGroupEdit(null);
        } catch {
        } finally {
        }
      }
    };

    const columns: OrganizationResourceColumn<Group>[] = [
      {
        id: "name",
        label: getTranslation(translations, "placeholders.group.name.label"),
        sortType: SupportedUserDataTypes.string,
        sortValue: (group) => group.name || "",
        render: ({ icon, name }) => (
          <Stack direction="row" alignItems="center" spacing={2}>
            <AvatarRem icon={icon || ""} />
            <Typography fontWeight={700}>{name}</Typography>
          </Stack>
        ),
      },
      {
        id: "administrators",
        label: getTranslation(translations, "generic.head_of_department"),
        sortType: SupportedUserDataTypes.string,
        sortValue: (group) => getAdministratorsLabel(group),
        render: (group) => (
          <Typography color="text.secondary">{getAdministratorsLabel(group)}</Typography>
        ),
      },
      {
        id: "users",
        label: getTranslation(translations, "generic.users.number"),
        sortType: SupportedUserDataTypes.number,
        sortValue: (group) => getUsersCount(group),
        render: (group) => (
          <Box
            component="span"
            sx={{
              borderRadius: 999,
              bgcolor: "grey.100",
              border: "1px solid",
              borderColor: "divider",
              px: 1.5,
              py: 0.5,
              fontWeight: 700,
            }}
          >
            {getUsersCount(group)}
          </Box>
        ),
      },
      {
        id: "actions",
        label: getTranslation(translations, "generic.actions"),
        align: "right",
        render: (group) =>
          administrator ? (
            <Stack direction="row" justifyContent="flex-end" spacing={1}>
              <Tooltip title={getTranslation(translations, "generic.edit_group.caption")}>
                <IconButton
                  id={`btn-modify-group-${group.id}`}
                  size="small"
                  color="secondary"
                  onClick={(e) => {
                    e.stopPropagation();
                    setGroupEdit(group);
                  }}
                >
                  <Edit fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title={getTranslation(translations, "generic.remove")}>
                <IconButton
                  id={`btn-delete-group-${group.id}`}
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteGroup(group);
                  }}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          ) : null,
      },
    ];

    return (
      <React.Fragment>
        <Stack height="100%" overflow="hidden" flex={1}>
          {!loading && groups.length === 0 ? (
            <Stack height="100%" alignItems="center" justifyContent="center">
              <Box
                component="img"
                src={groupsEmptyImage}
                alt="non ci sono gruppi"
                sx={{ width: 400, height: 400, objectFit: "contain" }}
              />
              <Stack direction={{ xs: "column", sm: "row" }} alignItems="center" spacing={1.5}>
                <Typography variant="h6">{getTranslation(translations, "generic.no.groups")}</Typography>
                {administrator && onCreateGroup && (
                  <FlowentiButton
                    id="btn-empty-groups-card"
                    size="small"
                    variant="contained"
                    startIcon={<Add />}
                    onClick={onCreateGroup}
                  >
                    {getTranslation(translations, "groups.button.create")}
                  </FlowentiButton>
                )}
              </Stack>
            </Stack>
          ) : isMobile ? (
            <OrganizationResourceCards
              id="groups-list"
              rows={groups}
              columns={columns}
              loading={loading}
              getRowKey={(group) => `group-${group.id}-${getVersion(group.id)}`}
              getRowId={(group) => `group-${group.id}`}
              canHover={() => administrator}
              onRowClick={(group) => {
                if (administrator) {
                  setGroupEdit(group);
                }
              }}
              getLastRowRef={(group, index) =>
                groups.length === index + 1 ? lastGroupRef : undefined
              }
            />
          ) : (
            <OrganizationResourceTable
              id="groups-list"
              rows={groups}
              columns={columns}
              loading={loading}
              getRowKey={(group) => `group-${group.id}-${getVersion(group.id)}`}
              getRowId={(group) => `group-${group.id}`}
              canHover={() => administrator}
              onRowClick={(group) => {
                if (administrator) {
                  setGroupEdit(group);
                }
              }}
              getLastRowRef={(group, index) =>
                groups.length === index + 1 ? lastGroupRef : undefined
              }
            />
          )}
        </Stack>
        <GroupDialog
          loading={loading}
          open={Boolean(groupEdit)}
          group={groupEdit}
          organizationId={organizationId}
          onUsersUpdate={getFirstGroups}
          onDelete={handleDeleteGroup}
          onClose={() => setGroupEdit(null)}
          onSave={handleSaveEdit}
        />
      </React.Fragment >
    );
  }
);

export default GroupsPanel;
